import { useRef, useState } from 'react'
import { AxiosError } from 'axios'

import CPFInput from '@/components/form/input/cpf-mask-input'
import { WhatsAppInput } from '@/components/form/input'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useClientContext } from '@/context/client'
import { toast } from '@/lib/hooks/use-toast'
import { useCheckOwnerByCpf } from '@/features/client/services'
import { ContadorForm } from './contador-form'
import { isValidPersonName } from '@/utils/format'

export function ClientOwnerIdentificationForm() {
  const { mostrarCamposContador, setClienteData, setMostrarCamposContador, setClientMode } = useClientContext()
  const contadorRef = useRef<HTMLDivElement>(null)
  const regimeRef = useRef<HTMLDivElement>(null)

  const [cpf, setCpf] = useState('')
  const [isCpfValid, setIsCpfValid] = useState(false)
  const [whatsapp, setWhatsapp] = useState('')
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [ownerLookupResult, setOwnerLookupResult] = useState<'idle' | 'existing' | 'new'>('idle')

  const { mutateAsync: checkOwnerByCpf, isPending: isCheckingOwner } = useCheckOwnerByCpf()

  const baseFieldsAreValid = isCpfValid && whatsapp.trim() !== '' && email.trim() !== ''

  const scrollToContador = () => {
    setTimeout(() => contadorRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
  }

  const handleContinuar = () => {
    setTimeout(() => regimeRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
  }

  const handleExistingOwnerFlow = () => {
    setClientMode('existing-owner')
    setClienteData({
      name: '',
      whatsapp,
      email,
      cpf
    })
    setMostrarCamposContador(true)
    scrollToContador()
  }

  const handleNewOwnerFlow = () => {
    if (!baseFieldsAreValid || name.trim() === '') {
      toast({
        title: 'Preencha nome, CPF, WhatsApp e e-mail corretamente.',
        variant: 'destructive'
      })
      return
    }

    if (!isValidPersonName(name)) {
      toast({
        title: 'Nome do proprietário não pode conter números.',
        variant: 'destructive'
      })
      return
    }

    setClientMode('new-owner')
    setClienteData({
      name,
      whatsapp,
      email,
      cpf
    })
    setMostrarCamposContador(true)
    scrollToContador()
  }

  const handleCheckCpf = async () => {
    if (!baseFieldsAreValid) {
      toast({
        title: 'Preencha CPF, WhatsApp e e-mail corretamente.',
        variant: 'destructive'
      })
      return
    }

    try {
      const response = await checkOwnerByCpf(cpf)

      if (response.exists) {
        setOwnerLookupResult('existing')
        handleExistingOwnerFlow()
        return
      }

      setOwnerLookupResult('new')
      setMostrarCamposContador(false)
      setClientMode(null)
      toast({
        title: 'CPF não encontrado. Informe o nome do proprietário para continuar.'
      })
    } catch (error) {
      const errorMessage =
        error instanceof AxiosError ? error.response?.data?.error || error.message : 'Erro ao verificar CPF.'

      toast({
        title: errorMessage,
        variant: 'destructive'
      })
    }
  }

  return (
    <div className="min-w-64 w-full mx-auto pt-3">
      <div className="space-y-4">
        <div className="py-2 mb-2 border-b">
          <h2 className="font-bold text-base">Dados do Proprietário</h2>
          <p className="text-sm">
            Informe o CPF do proprietário. Se ele já existir, o sistema fará o vínculo da nova empresa. Caso não
            exista, o cadastro seguirá com senha na etapa final.
          </p>
        </div>

        <div className="space-y-2">
          <CPFInput
            value={cpf}
            onCPFChange={(value, valid) => {
              setCpf(value)
              setIsCpfValid(valid)
              setOwnerLookupResult('idle')
              setMostrarCamposContador(false)
              setClientMode(null)
            }}
          />

          <WhatsAppInput
            placeholder="WhatsApp"
            value={whatsapp}
            onChange={(value: string) => setWhatsapp(value)}
          />

          <Input placeholder="E-mail" value={email} type="email" onChange={e => setEmail(e.target.value)} />

          <Button onClick={handleCheckCpf} disabled={!baseFieldsAreValid || isCheckingOwner}>
            {isCheckingOwner ? 'Verificando CPF...' : 'Verificar CPF'}
          </Button>
        </div>

        {ownerLookupResult === 'new' && (
          <div className="space-y-2 rounded-lg border border-border p-4">
            <Input placeholder="Nome completo do proprietário" value={name} onChange={e => setName(e.target.value)} />
            <p className="text-sm text-muted-foreground">
              Este CPF ainda não possui proprietário cadastrado. O restante do fluxo seguirá com criação de senha.
            </p>
            <Button onClick={handleNewOwnerFlow} disabled={!baseFieldsAreValid || name.trim() === ''}>
              Continuar cadastro
            </Button>
          </div>
        )}

        {mostrarCamposContador && (
          <div ref={contadorRef}>
            <ContadorForm onContinue={handleContinuar} />
          </div>
        )}

        <div ref={regimeRef} />
      </div>
    </div>
  )
}
